import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Power, Moon, Clock, Smartphone, Battery, BatteryLow, Lightbulb, Calendar, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

interface RemoteShutdownWidgetProps {
    color?: string;
}

const RemoteShutdownWidget: React.FC<RemoteShutdownWidgetProps> = ({ color = "from-neutral-400 to-stone-500" }) => {
    const { t, i18n } = useTranslation("nonHealth");
    const [shuttingDown, setShuttingDown] = useState(false);
    const [shutDown, setShutDown] = useState(false);
    const [batteryLevel, setBatteryLevel] = useState(78);

    useEffect(() => {
        setBatteryLevel(Math.floor(Math.random() * (100 - 20) + 20));
    }, []);

    const handleShutdown = () => {
        if (shuttingDown || shutDown) return;
        setShuttingDown(true);
        setTimeout(() => {
            setShuttingDown(false);
            setShutDown(true);
            setTimeout(() => setShutDown(false), 3000);
        }, 2500);
    };

    const schedules = useMemo(() => [
        { label: t("widgets.remoteShutdown.schedules.nightSleep"), time: "22:00", icon: <Moon className="w-3.5 h-3.5" />, color: "text-indigo-600", active: true },
        { label: t("widgets.remoteShutdown.schedules.dailyRest"), time: "14:00", icon: <Clock className="w-3.5 h-3.5" />, color: "text-amber-600", active: false },
        { label: t("widgets.remoteShutdown.schedules.appointment"), time: t("widgets.remoteShutdown.schedules.manual"), icon: <Calendar className="w-3.5 h-3.5" />, color: "text-rose-600", active: false },
    ], [t]);

    return (
        <motion.div
            key="remoteShutdown-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
        >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 shadow-xl border border-stone-200 relative overflow-hidden">
                {/* افکت‌های تزئینی پس‌زمینه */}
                <motion.div
                    className="absolute -left-10 top-10 w-40 h-40 bg-gradient-to-br from-stone-400/15 to-neutral-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 8, repeat: Infinity }}
                />
                <motion.div
                    className="absolute -right-10 -bottom-10 w-36 h-36 bg-gradient-to-tr from-neutral-400/15 to-stone-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 8, repeat: Infinity, delay: 1 }}
                />

                {/* ستاره‌های متحرک پس‌زمینه */}
                <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
                    {[...Array(8)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute w-1 h-1 rounded-full bg-slate-400"
                            style={{ top: `${10 + i * 12}%`, left: `${5 + i * 11}%` }}
                            animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5] }}
                            transition={{ duration: 2 + i * 0.5, repeat: Infinity, delay: i * 0.3 }}
                        />
                    ))}
                </div>

                {/* هدر */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <motion.div
                            className={`w-9 h-9 rounded-full bg-gradient-to-r ${color} flex items-center justify-center text-white shadow-md`}
                            animate={shuttingDown ? { scale: [1, 0.9, 1], opacity: [1, 0.7, 1] } : { scale: [1, 1.05, 1] }}
                            transition={shuttingDown ? { duration: 0.8, repeat: Infinity } : { duration: 2, repeat: Infinity }}
                        >
                            <Power className="w-5 h-5" />
                        </motion.div>
                        <h5 className="text-stone-800 font-semibold text-lg">{t("widgets.remoteShutdown.title")}</h5>
                    </div>
                    <motion.div
                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-white shadow-sm ${
                            shutDown ? "bg-gradient-to-r from-stone-500 to-gray-600" : "bg-gradient-to-r from-green-500 to-emerald-600"
                        }`}
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <motion.span
                            className="inline-block w-2 h-2 rounded-full bg-white ml-1"
                            animate={{ opacity: shuttingDown ? [1, 0.3, 1] : 1 }}
                            transition={{ duration: 0.8, repeat: Infinity }}
                        />
                        {shutDown ? t("widgets.remoteShutdown.status.off") : shuttingDown ? t("widgets.remoteShutdown.status.shuttingDown") : t("widgets.remoteShutdown.status.on")}
                    </motion.div>
                </div>

                {/* نمایش وضعیت دستگاه */}
                <div className="bg-gradient-to-br from-stone-50 to-neutral-100 p-4 rounded-xl mb-4 shadow-inner border border-stone-200/50 relative overflow-hidden">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <motion.div
                                className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm"
                                animate={shuttingDown ? { opacity: [1, 0.3, 1] } : {}}
                                transition={{ duration: 0.8, repeat: Infinity }}
                            >
                                <Smartphone className="w-4 h-4 text-stone-600" />
                            </motion.div>
                            <div>
                                <p className="text-stone-700 text-xs font-medium">{t("widgets.remoteShutdown.deviceInfo.watchName")}</p>
                                <motion.p
                                    className={`font-bold text-sm ${shutDown ? "text-stone-500" : "text-stone-800"}`}
                                    animate={shuttingDown ? { opacity: [1, 0.5, 1] } : {}}
                                    transition={{ duration: 0.8, repeat: Infinity }}
                                >
                                    {shutDown ? t("widgets.remoteShutdown.deviceInfo.status.off") : shuttingDown ? t("widgets.remoteShutdown.deviceInfo.status.shuttingDown") : t("widgets.remoteShutdown.deviceInfo.status.running")}
                                </motion.p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-stone-600">
                            <Battery className="w-4 h-4 text-green-600" />
                            <span className="font-semibold text-green-600">{new Intl.NumberFormat(i18n.language).format(batteryLevel)}٪</span>
                        </div>
                    </div>

                    {/* زمان‌بندی خاموشی */}
                    <div className="space-y-2">
                        <p className="text-xs text-stone-600 font-medium mb-1">{t("widgets.remoteShutdown.autoShutdownSchedule")}:</p>
                        {schedules.map((schedule, idx) => (
                            <motion.div
                                key={idx}
                                className={`flex items-center justify-between px-3 py-2 rounded-lg border ${schedule.active ? "bg-white border-stone-300 shadow-sm" : "bg-white/40 border-stone-100"}`}
                                whileHover={{ y: -1, boxShadow: "0 4px 8px rgba(0,0,0,0.08)" }}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * idx }}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={schedule.color}>{schedule.icon}</span>
                                    <span className="text-xs text-stone-700">{schedule.label}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-stone-600">{schedule.time}</span>
                                    <motion.div className={`w-7 h-4 rounded-full flex items-center px-0.5 ${schedule.active ? "bg-stone-500 justify-end" : "bg-gray-200 justify-start"}`}>
                                        <motion.div className="w-3 h-3 bg-white rounded-full shadow-sm" />
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* دکمه خاموشی */}
                <motion.button
                    onClick={handleShutdown}
                    className={`w-full text-white py-3 rounded-xl text-sm font-semibold relative overflow-hidden shadow-lg mb-3 ${
                        shutDown ? "bg-gradient-to-r from-stone-500 to-gray-600" : shuttingDown ? "bg-gradient-to-r from-amber-500 to-orange-500 cursor-not-allowed" : `bg-gradient-to-r ${color}`
                    }`}
                    whileHover={!shuttingDown && !shutDown ? { scale: 1.02, y: -2, boxShadow: "0px 8px 20px rgba(0,0,0,0.2)" } : {}}
                    whileTap={!shuttingDown && !shutDown ? { scale: 0.98 } : {}}
                >
                    <motion.span
                        className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                        animate={shuttingDown ? { x: ["-100%", "100%"] } : { x: "-100%" }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-2 font-semibold">
                        <motion.div animate={shuttingDown ? { scale: [1, 0.9, 1] } : {}} transition={{ duration: 0.8, repeat: Infinity }}>
                            {shutDown ? <CheckCircle className="w-5 h-5" /> : <Power className="w-5 h-5" />}
                        </motion.div>
                        {shutDown ? t("widgets.remoteShutdown.shutdownSuccess") : shuttingDown ? t("widgets.remoteShutdown.status.shuttingDown") : t("widgets.remoteShutdown.shutdownButton")}
                    </span>
                </motion.button>

                {/* پیام راهنما */}
                <motion.div
                    className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-700 text-xs p-2.5 rounded-lg flex items-center"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <motion.span
                        className="ml-2 bg-amber-100 w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                        animate={{ rotate: [0, 10, -10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <Lightbulb className="w-3.5 h-3.5" />
                    </motion.span>
                    <span>{t("widgets.remoteShutdown.tip")}</span>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default RemoteShutdownWidget;